import React from 'react';
import { motion } from 'framer-motion';
interface SyllableBlocksProps {
  word: string;
  syllables: string[];
  types: ('open' | 'closed')[];
  highlight?: number;
  label?: string;
  /** 'stimulus' = compact visual only (shown in-question), 'support' = full educational panel (shown after answer) */
  mode?: 'stimulus' | 'support';
}
/**
 * SyllableBlocks - Word syllable breakdown visual
 *
 * Shows word broken into syllables with open/closed indication.
 * Used for SPR (Spelling Regels) module sound group questions.
 *
 * stimulus mode: compact — word + syllable blocks only
 * support mode: full educational panel with explanations
 */
export function SyllableBlocks({
  word,
  syllables,
  types,
  highlight,
  label,
  mode = 'support'
}: SyllableBlocksProps) {
  // Find vowels in each syllable
  const getVowels = (syllable: string): number[] => {
    const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];
    const positions: number[] = [];
    syllable.split('').forEach((char, index) => {
      if (vowels.includes(char.toLowerCase())) {
        positions.push(index);
      }
    });
    return positions;
  };
  // ── STIMULUS MODE: compact visual only ──────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="w-full max-w-md mx-auto" data-id="element-10312">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200" data-id="element-10313">
          {/* Header - compact */}
          {label && <div className="mb-3 text-center" data-id="element-10314">
              <span className="text-xs font-bold text-green-800" data-id="element-10315">{label}</span>
            </div>}

          {/* Complete Word - compact */}
          <div className="mb-3 text-center" data-id="element-10316">
            <span className="text-lg font-black text-slate-800" data-id="element-10317">{word}</span>
          </div>

          {/* Syllable Blocks - compact */}
          <div className="flex justify-center items-center gap-2 flex-wrap" data-id="element-10318">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = highlight === index;
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 10
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.1,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-10319">
                  
                  <div className={`
                    relative px-4 py-2.5 rounded-xl border-3 shadow-md
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-3 ring-yellow-300 scale-105' : ''}
                  `} data-id="element-10320">
                    
                    <div className="flex gap-0.5" data-id="element-10321">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    return <span key={charIndex} className={`
                              text-xl font-black
                              ${isVowel ? 'text-white drop-shadow-md' : 'text-white/90'}
                            `} data-id="element-10322">
                            
                            {char}
                          </span>;
                  })}
                    </div>
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-sm" data-id="element-10323">
                      <span className="text-[10px] font-bold" data-id="element-10324">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>
                  <div className={`
                    mt-1.5 px-2 py-0.5 rounded text-[10px] font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-10325">
                    
                    {type === 'open' ? 'Open' : 'Gesloten'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>;
  }
  // ── SUPPORT MODE: full educational panel (original behavior) ────────────
  return <div className="w-full max-w-3xl mx-auto" data-id="element-10326">
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 border-2 border-green-200 shadow-lg" data-id="element-10327">
        {/* Header */}
        <div className="mb-6 text-center" data-id="element-10328">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-green-200 shadow-sm" data-id="element-10329">
            <span className="text-2xl" data-id="element-10330">🔤</span>
            <div className="text-left" data-id="element-10331">
              <div className="text-sm font-bold text-green-900" data-id="element-10332">
                {label || 'Lettergrepen Verdeling'}
              </div>
              <div className="text-xs text-green-600 font-semibold" data-id="element-10333">
                Open en Gesloten Lettergrepen
              </div>
            </div>
          </div>
        </div>

        {/* Complete Word */}
        <div className="mb-6 text-center" data-id="element-10334">
          <div className="inline-flex flex-col items-center gap-2 bg-white/80 rounded-xl px-6 py-4 border border-slate-200 shadow-sm" data-id="element-10335">
            <div className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-10336">
              Heel Woord
            </div>
            <div className="text-3xl font-black text-slate-800" data-id="element-10337">{word}</div>
          </div>
        </div>

        {/* Syllable Blocks */}
        <div className="mb-6" data-id="element-10338">
          <div className="flex justify-center items-center gap-3 flex-wrap" data-id="element-10339">
            {syllables.map((syllable, index) => {
            const type = types[index];
            const isHighlighted = highlight === index;
            const vowelPositions = getVowels(syllable);
            return <motion.div key={index} initial={{
              scale: 0,
              y: 20
            }} animate={{
              scale: 1,
              y: 0
            }} transition={{
              delay: index * 0.15,
              type: 'spring',
              stiffness: 300
            }} className="flex flex-col items-center" data-id="element-10340">
                  
                  {/* Syllable Block */}
                  <div className={`
                    relative px-6 py-4 rounded-2xl border-4 shadow-xl
                    transition-all duration-300
                    ${type === 'open' ? 'bg-gradient-to-br from-green-400 to-emerald-500 border-green-600' : 'bg-gradient-to-br from-red-400 to-rose-500 border-red-600'}
                    ${isHighlighted ? 'ring-4 ring-yellow-300 scale-110' : ''}
                  `} data-id="element-10341">
                    
                    {/* Letters */}
                    <div className="flex gap-1" data-id="element-10342">
                      {syllable.split('').map((char, charIndex) => {
                    const isVowel = vowelPositions.includes(charIndex);
                    return <span key={charIndex} className={`
                              text-2xl md:text-3xl font-black
                              ${isVowel ? 'text-white drop-shadow-lg' : 'text-white/90'}
                            `} data-id="element-10343">
                            
                            {char}
                          </span>;
                  })}
                    </div>

                    {/* Type indicator */}
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border-2 border-current flex items-center justify-center shadow-md" data-id="element-10344">
                      <span className="text-xs font-bold" data-id="element-10345">
                        {type === 'open' ? '○' : '●'}
                      </span>
                    </div>
                  </div>

                  {/* Label */}
                  <div className={`
                    mt-2 px-3 py-1 rounded-lg text-xs font-bold
                    ${type === 'open' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}
                  `} data-id="element-10346">
                    
                    {type === 'open' ? 'Open (lange klank)' : 'Gesloten (korte klank)'}
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* Educational Explanations */}
        <div className="space-y-4" data-id="element-10347">
          {/* What do you see? */}
          <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-10348">
            <div className="flex items-start gap-3" data-id="element-10349">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-10350">
                <span className="text-blue-600 text-lg" data-id="element-10351">👀</span>
              </div>
              <div data-id="element-10352">
                <h4 className="text-sm font-bold text-blue-900 mb-2" data-id="element-10353">
                  Wat zie je?
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-10354">
                  <p data-id="element-10355">
                    Het woord <strong data-id="element-10356">{word}</strong> bestaat uit{' '}
                    <strong data-id="element-10357">{syllables.length}</strong> lettergreep
                    {syllables.length > 1 ? 'en' : ''}:{' '}
                    {syllables.map((s, i) => <span key={i} data-id="element-10358">
                        <strong className={types[i] === 'open' ? 'text-green-700' : 'text-red-700'} data-id="element-10359">
                        
                          {s}
                        </strong>
                        {i < syllables.length - 1 ? ' + ' : ''}
                      </span>)}
                  </p>
                  <div className="pt-2 border-t border-blue-100 space-y-1" data-id="element-10360">
                    <p className="flex items-center gap-2" data-id="element-10361">
                      <span className="w-4 h-4 rounded bg-green-500" data-id="element-10362"></span>
                      <span data-id="element-10363">
                        <strong data-id="element-10364">Groen (○):</strong> Open lettergreep = lange
                        klank (één medeklinker na de klinker)
                      </span>
                    </p>
                    <p className="flex items-center gap-2" data-id="element-10365">
                      <span className="w-4 h-4 rounded bg-red-500" data-id="element-10366"></span>
                      <span data-id="element-10367">
                        <strong data-id="element-10368">Rood (●):</strong> Gesloten lettergreep = korte
                        klank (twee medeklinkers na de klinker)
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Open vs Closed explanation */}
          <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-10369">
            <div className="flex items-start gap-3" data-id="element-10370">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-10371">
                <span className="text-emerald-600 text-lg" data-id="element-10372">📖</span>
              </div>
              <div data-id="element-10373">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-10374">
                  Open vs Gesloten Lettergrepen
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-3" data-id="element-10375">
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200" data-id="element-10376">
                    <p className="font-semibold text-green-800 mb-1" data-id="element-10377">
                      Open lettergreep (○):
                    </p>
                    <p data-id="element-10378">
                      Eindigt op een <strong data-id="element-10379">klinker</strong> (a, e, i, o, u).
                      De klinker klinkt <strong data-id="element-10380">lang</strong>.
                    </p>
                    <p className="text-xs mt-1 text-green-700" data-id="element-10381">
                      Voorbeeld: <strong data-id="element-10382">ja-ger</strong> (ja = open, lange 'a')
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200" data-id="element-10383">
                    <p className="font-semibold text-red-800 mb-1" data-id="element-10384">
                      Gesloten lettergreep (●):
                    </p>
                    <p data-id="element-10385">
                      Eindigt op een <strong data-id="element-10386">medeklinker</strong>. De klinker
                      klinkt <strong data-id="element-10387">kort</strong>.
                    </p>
                    <p className="text-xs mt-1 text-red-700" data-id="element-10388">
                      Voorbeeld: <strong data-id="element-10389">bak-ker</strong> (bak = gesloten, korte
                      'a')
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Spelling rule */}
          <div className="bg-indigo-50/80 rounded-xl p-5 border border-indigo-200 shadow-sm" data-id="element-10390">
            <div className="flex items-start gap-3" data-id="element-10391">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-10392">
                <span className="text-indigo-600 text-lg" data-id="element-10393">✍️</span>
              </div>
              <div data-id="element-10394">
                <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-10395">
                  Spellingregel
                </h4>
                <div className="text-sm text-slate-700 leading-relaxed space-y-2" data-id="element-10396">
                  <p data-id="element-10397">
                    <strong data-id="element-10398">Korte klank (gesloten):</strong> Schrijf meestal{' '}
                    <strong data-id="element-10399">twee medeklinkers</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-10400">
                    Voorbeeld: bak-ker (korte 'a' → dubbele k), zit-ten (korte
                    'i' → dubbele t)
                  </p>
                  <p className="pt-2 border-t border-indigo-200" data-id="element-10401">
                    <strong data-id="element-10402">Lange klank (open):</strong> Schrijf meestal{' '}
                    <strong data-id="element-10403">één medeklinker</strong> na de klinker.
                  </p>
                  <p className="text-xs text-indigo-700 bg-indigo-50 p-2 rounded border border-indigo-200" data-id="element-10404">
                    Voorbeeld: ja-ger (lange 'a' → enkele g), bo-men (lange 'o'
                    → enkele m)
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-10405">
            <div className="flex items-start gap-3" data-id="element-10406">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-10407">
                <span className="text-amber-600 text-lg" data-id="element-10408">💡</span>
              </div>
              <div data-id="element-10409">
                <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-10410">
                  Handige tip
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-10411">
                  Spreek het woord hardop uit! Hoor je een{' '}
                  <strong data-id="element-10412">korte</strong> klinker? Dan is de lettergreep meestal{' '}
                  <strong data-id="element-10413">gesloten</strong> en schrijf je{' '}
                  <strong data-id="element-10414">twee medeklinkers</strong>. Hoor je een{' '}
                  <strong data-id="element-10415">lange</strong> klinker? Dan is de lettergreep{' '}
                  <strong data-id="element-10416">open</strong> en schrijf je{' '}
                  <strong data-id="element-10417">één medeklinker</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}